<template>
  <div class="app-container finance-page">
    <finance-query-panel
      title="财务总览"
      description="综合查看签约、实际回款、正常开票及当前应收余额；专项明细请进入回款分析、开票分析或应收台账。"
      :report-year.sync="reportYear"
      :query-params.sync="queryParams"
      :dashboard="dashboard"
      :loading="loading"
      @query="queryData"
      @reset="resetQuery"
    />

    <el-row :gutter="14" class="metric-row">
      <el-col v-for="item in metricCards" :key="item.label" :xs="12" :sm="8" :lg="4">
        <el-card shadow="never" class="metric-card">
          <div class="metric-label">{{ item.label }}</div>
          <div class="metric-value">{{ item.value }}</div>
          <div class="metric-note">{{ item.note }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-alert
      v-if="Number(dashboard.summary.historicalImportedInvoiceAmount || 0) > 0"
      type="info"
      :closable="false"
      show-icon
      class="finance-alert"
      title="累计开票包含历史系统导入期初；月度开票趋势仅统计财务正常登记的真实业务记录。"
    />

    <el-row :gutter="14">
      <el-col :xs="24" :lg="16">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">{{ reportYear }}年月度签约、回款与正常开票</div>
          <finance-chart :option="trendOption" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">已签约合同回款与开票进度</div>
          <finance-chart :option="completionOption" />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="14">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">部门签约、回款与正常开票对比</div>
          <finance-chart :option="departmentOption" />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never" class="chart-card">
          <div class="card-title">产品大类回款与正常开票对比</div>
          <finance-chart :option="productOption" />
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div class="card-title">重点应收合同 Top 10（按当前未回款排序）</div>
      <el-table :data="topOutstandingRows" border stripe v-loading="loading">
        <el-table-column prop="contractNum" label="合同编号" min-width="145">
          <template slot-scope="scope">
            <el-button type="text" @click="openContract(scope.row)">{{ scope.row.contractNum }}</el-button>
          </template>
        </el-table-column>
        <el-table-column prop="customerName" label="客户" min-width="180" show-overflow-tooltip />
        <el-table-column prop="departmentName" label="部门" min-width="130" />
        <el-table-column prop="assignUserName" label="负责人" width="100" />
        <el-table-column label="合同金额" width="140" align="right">
          <template slot-scope="scope">{{ amount(scope.row.contractAmount) }}</template>
        </el-table-column>
        <el-table-column label="累计回款" width="140" align="right">
          <template slot-scope="scope">{{ amount(scope.row.receivedAmount) }}</template>
        </el-table-column>
        <el-table-column label="累计开票" width="140" align="right">
          <template slot-scope="scope">{{ amount(scope.row.invoiceAmount) }}</template>
        </el-table-column>
        <el-table-column label="当前未回款" width="140" align="right">
          <template slot-scope="scope"><span class="danger-amount">{{ amount(scope.row.outstandingAmount) }}</span></template>
        </el-table-column>
        <el-table-column label="当前未开票" width="140" align="right">
          <template slot-scope="scope">{{ amount(uninvoicedAmount(scope.row)) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import FinanceChart from './components/FinanceChart'
import FinanceQueryPanel from './components/FinanceQueryPanel'
import receivableFinanceMixin from './components/receivableFinanceMixin'
import { groupTotals, sumMonths } from './components/financeShared'

export default {
  name: 'FinanceOverview',
  financeScene: 'overview',
  components: { FinanceChart, FinanceQueryPanel },
  mixins: [receivableFinanceMixin],
  computed: {
    metricCards () {
      const summary = this.dashboard.summary || {}
      return [
        { label: `${this.reportYear}年签约`, value: this.amount(summary.contractAmount), note: '仅已签约合同 / 元' },
        { label: `${this.reportYear}年回款`, value: this.amount(summary.fundAmount), note: '按实际回款日期 / 元' },
        { label: `${this.reportYear}年正常开票`, value: this.amount(summary.reportYearBusinessInvoiceAmount), note: '按真实开票日期 / 元' },
        { label: '当前未回款', value: this.amount(summary.outstandingAmount), note: '已签约合同口径 / 元' },
        { label: '当前未开票', value: this.amount(summary.uninvoicedAmount), note: '已签约合同口径 / 元' },
        { label: '已签约合同', value: summary.signedContractCount || 0, note: `台账共${summary.contractCount || 0}份 / 份` }
      ]
    },
    trendOption () {
      const contract = sumMonths(this.dashboard.contractMonthlyRows)
      const fund = sumMonths(this.dashboard.fundMonthlyRows)
      const invoice = sumMonths(this.dashboard.businessInvoiceMonthlyRows)
      return {
        color: ['#4b86f8', '#22b573', '#7c6df2'],
        tooltip: { trigger: 'axis', valueFormatter: value => this.amount(value) },
        legend: { top: 4, data: ['签约金额', '实际回款', '正常开票'] },
        grid: { left: 24, right: 24, top: 50, bottom: 20, containLabel: true },
        xAxis: { type: 'category', data: Array.from({ length: 12 }, (item, index) => `${index + 1}月`) },
        yAxis: { type: 'value', axisLabel: { formatter: value => `${(value / 10000).toFixed(0)}万` } },
        series: [
          { name: '签约金额', type: 'bar', barMaxWidth: 24, data: contract },
          { name: '实际回款', type: 'line', smooth: true, symbolSize: 7, data: fund },
          { name: '正常开票', type: 'line', smooth: true, symbolSize: 7, data: invoice }
        ]
      }
    },
    completionOption () {
      const summary = this.dashboard.summary || {}
      const contractAmount = Number(summary.signedContractAmount || 0)
      const progress = [
        this.progressItem('回款完成率', summary.signedReceivedAmount, contractAmount, '#22b573'),
        this.progressItem('开票完成率', summary.signedInvoiceAmount, contractAmount, '#7c6df2')
      ]
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const item = Array.isArray(params) ? params[0] : params
            const data = item.data || {}
            return `${item.name}<br/>金额：${this.amount(data.amount)} 元<br/>合同总额：${this.amount(contractAmount)} 元<br/>完成率：${data.actualRate}%`
          }
        },
        grid: { left: 18, right: 60, top: 40, bottom: 24, containLabel: true },
        xAxis: { type: 'value', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
        yAxis: { type: 'category', data: progress.map(item => item.name) },
        series: [{
          type: 'bar',
          barWidth: 24,
          showBackground: true,
          backgroundStyle: { color: '#edf1f7', borderRadius: 12 },
          label: { show: true, position: 'right', formatter: params => `${params.data.actualRate}%` },
          data: progress.map(item => ({
            value: item.value,
            amount: item.amount,
            actualRate: item.actualRate,
            itemStyle: { color: item.color, borderRadius: 12 }
          }))
        }]
      }
    },
    departmentOption () {
      const contracts = groupTotals(this.dashboard.contractMonthlyRows, 'departmentName')
      const funds = groupTotals(this.dashboard.fundMonthlyRows, 'departmentName')
      const invoices = groupTotals(this.dashboard.businessInvoiceMonthlyRows, 'departmentName')
      const names = this.comparisonNames([contracts, funds, invoices])
      const valueOf = (rows, name) => (rows.find(item => item.name === name) || {}).value || 0
      return this.horizontalBarOption(names, [
        { name: '签约金额', data: names.map(name => valueOf(contracts, name)), color: '#4b86f8' },
        { name: '实际回款', data: names.map(name => valueOf(funds, name)), color: '#22b573' },
        { name: '正常开票', data: names.map(name => valueOf(invoices, name)), color: '#7c6df2' }
      ])
    },
    productOption () {
      const funds = groupTotals(this.dashboard.fundMonthlyRows, 'productCategory')
      const invoices = groupTotals(this.dashboard.businessInvoiceMonthlyRows, 'productCategory')
      const names = this.comparisonNames([funds, invoices], 6)
      const valueOf = (rows, name) => (rows.find(item => item.name === name) || {}).value || 0
      return this.horizontalBarOption(names, [
        { name: '实际回款', data: names.map(name => valueOf(funds, name)), color: '#22b573' },
        { name: '正常开票', data: names.map(name => valueOf(invoices, name)), color: '#7c6df2' }
      ])
    },
    topOutstandingRows () {
      return this.dashboard.ledgerRows || []
    }
  },
  methods: {
    progressItem (name, amount, total, color) {
      const numericAmount = Number(amount || 0)
      const actualRate = total > 0 ? Number((numericAmount * 100 / total).toFixed(2)) : 0
      return { name, amount: numericAmount, actualRate, value: Math.min(100, Math.max(0, actualRate)), color }
    },
    comparisonNames (rowGroups, limit = 10) {
      const totals = {}
      rowGroups.forEach(rows => {
        rows.forEach(item => {
          totals[item.name] = (totals[item.name] || 0) + Number(item.value || 0)
        })
      })
      return Object.keys(totals)
        .sort((left, right) => totals[right] - totals[left])
        .slice(0, limit)
        .reverse()
    },
    uninvoicedAmount (row) {
      return Math.max(0, Number(row.contractAmount || 0) - Number(row.invoiceAmount || 0))
    },
    horizontalBarOption (names, series) {
      return {
        color: series.map(item => item.color),
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: value => this.amount(value) },
        legend: { top: 4 },
        grid: { left: 18, right: 24, top: 48, bottom: 16, containLabel: true },
        xAxis: { type: 'value', axisLabel: { formatter: value => `${(value / 10000).toFixed(0)}万` } },
        yAxis: { type: 'category', data: names, axisLabel: { width: 90, overflow: 'truncate' } },
        series: series.map(item => ({ name: item.name, type: 'bar', barMaxWidth: 20, data: item.data }))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.finance-page { min-height: calc(100vh - 84px); background: #f5f7fa; }
.metric-row { margin-top: 14px; }
.metric-card, .chart-card, .table-card { margin-bottom: 14px; }
.metric-label { color: #909399; font-size: 13px; }
.metric-value { margin-top: 10px; overflow: hidden; color: #303133; font-size: 21px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.metric-note { margin-top: 5px; color: #a6a9ad; font-size: 12px; }
.finance-alert { margin-bottom: 14px; }
.card-title { margin-bottom: 12px; color: #303133; font-size: 16px; font-weight: 600; }
.danger-amount { color: #e85d5d; font-weight: 600; }
</style>
